<!--
  StoreEmailVerification -- verify the shopper's email before a payroll order
  on an email-gated store (card nrNUCjgU, Jake 2026-10-08).

  The order bills the shopper whose address is on it, so the API refuses it
  until this session proves it reads that address: a one-time code is mailed
  to it (only to an approved domain) and typed back here. The component
  emits `verified` with the address once it is verified, and `verified` with
  null when the shopper edits the address, so checkout can hold Place Order
  for exactly the address the order will carry.

  Behavioural tests: `__tests__/store-email-verification.spec.js`.
-->
<template>
  <div class="store-email-verification" data-testid="email-verify">
    <div v-if="state === 'verified'" class="email-verify-done" data-testid="email-verify-done">
      <v-icon color="success" size="small" class="mr-1">mdi-check-circle</v-icon>
      {{ normalizedEmail }} is verified.
    </div>

    <template v-else>
      <div class="email-verify-intro">
        This store needs to confirm your email address before you order. We
        will send a 6-digit code to the email address above.
      </div>

      <div class="d-flex align-center mt-2">
        <v-btn
          variant="outlined"
          color="primary"
          size="small"
          style="text-transform: none"
          :disabled="!canSend || state === 'sending'"
          :loading="state === 'sending'"
          data-testid="email-verify-send"
          @click="sendCode"
        >
          {{ state === "sent" || state === "verifying" ? "Send a new code" : "Send code" }}
        </v-btn>
      </div>

      <div v-if="state === 'sent' || state === 'verifying'" class="d-flex align-center mt-2">
        <v-text-field
          v-model="code"
          label="Verification code"
          inputmode="numeric"
          autocomplete="one-time-code"
          density="compact"
          hide-details
          style="max-width: 200px"
          data-testid="email-verify-code"
        />
        <v-btn
          class="ml-2"
          color="primary"
          variant="flat"
          size="small"
          style="text-transform: none"
          :disabled="!code || state === 'verifying'"
          :loading="state === 'verifying'"
          data-testid="email-verify-submit"
          @click="verifyCode"
        >
          Verify
        </v-btn>
      </div>

      <div v-if="state === 'sent' && !error" class="email-verify-hint mt-1" data-testid="email-verify-sent">
        Code sent to {{ normalizedEmail }}. It expires in 10 minutes.
      </div>
      <div v-if="error" class="email-verify-error mt-1" data-testid="email-verify-error">
        {{ error }}
      </div>
    </template>
  </div>
</template>

<script>
import { SUPPRESS_ERROR_TOAST } from "@/interceptors/authResponseInterceptor";
import {
  storefrontEmailCodeSendUrl,
  storefrontEmailCodeVerifyUrl,
  storefrontGatePassUrl,
} from "./storefrontStoreApi";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GENERIC_ERROR = "Something went wrong. Please try again.";

// This component shows its own errors, so the global toast stays quiet.
const QUIET = { [SUPPRESS_ERROR_TOAST]: true };

function serverMessage(err) {
  return err?.response?.data?.error || GENERIC_ERROR;
}

export default {
  name: "StoreEmailVerification",

  props: {
    storeId: { type: [Number, String], required: true },
    email: { type: String, default: "" },
  },

  emits: ["verified"],

  data() {
    return {
      // 'idle' | 'sending' | 'sent' | 'verifying' | 'verified'
      state: "idle",
      code: "",
      error: "",
    };
  },

  computed: {
    normalizedEmail() {
      return typeof this.email === "string" ? this.email.trim().toLowerCase() : "";
    },
    canSend() {
      return EMAIL_SHAPE.test(this.normalizedEmail);
    },
  },

  watch: {
    // A different address is a different verification.
    email(next, prev) {
      if (next === prev) return;
      this.state = "idle";
      this.code = "";
      this.error = "";
      this.$emit("verified", null);
    },
  },

  mounted() {
    this.loadStatus();
  },

  methods: {
    /** Pick up an address this session already verified (a reload, a revisit). */
    async loadStatus() {
      if (!this.canSend) return;
      try {
        const resp = await this.$axios.get(storefrontGatePassUrl(this.storeId), QUIET);
        if (resp?.data?.email && resp.data.email === this.normalizedEmail) {
          this.state = "verified";
          this.$emit("verified", this.normalizedEmail);
        }
      } catch {
        // Not knowing is not an error the shopper can act on: they can still
        // verify by code.
      }
    },

    async sendCode() {
      if (!this.canSend) return;
      const previous = this.state;
      this.state = "sending";
      this.error = "";
      try {
        await this.$axios.post(
          storefrontEmailCodeSendUrl(this.storeId),
          { email: this.normalizedEmail },
          QUIET
        );
        this.code = "";
        this.state = "sent";
      } catch (err) {
        this.state = previous === "sent" ? "sent" : "idle";
        this.error = serverMessage(err);
      }
    },

    async verifyCode() {
      const code = (this.code || "").trim();
      if (!code) return;
      this.state = "verifying";
      this.error = "";
      try {
        await this.$axios.post(
          storefrontEmailCodeVerifyUrl(this.storeId),
          { email: this.normalizedEmail, code },
          QUIET
        );
        this.state = "verified";
        this.$emit("verified", this.normalizedEmail);
      } catch (err) {
        this.state = "sent";
        this.error = serverMessage(err);
      }
    },
  },
};
</script>

<style scoped>
.store-email-verification {
  margin: 8px 0 12px 0;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.email-verify-intro,
.email-verify-hint {
  font-size: 14px;
  color: #555;
}

.email-verify-error {
  font-size: 14px;
  color: #b00020;
}

.email-verify-done {
  font-size: 14px;
}
</style>
